import {
  Component,
  Input,
  Output,
  EventEmitter,
  forwardRef,
  ChangeDetectorRef,
  ViewChild,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { MatMenuTrigger } from '@angular/material/menu';

export interface StatusFilterOption {
  label: string;
  value: any;
  [key: string]: any;
}

@Component({
  selector: 'app-status-filter',
  templateUrl: './app-status-filter.component.html',
  styleUrls: ['./app-status-filter.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => AppStatusFilterComponent),
      multi: true,
    },
  ],
})
export class AppStatusFilterComponent implements ControlValueAccessor {
  @Input() labelText: string = 'Status';
  @Input() label: string = '';
  @Input() popoverTitle?: string;

  get resolvedLabel(): string {
    return this.label || this.labelText || 'Status';
  }
  @Input() options: StatusFilterOption[] = [];
  @Input() searchPlaceholder?: string;
  @Input() hideSearch: boolean = false;
  @Input() searchable: boolean = true;
  @Input() multiple: boolean = true;
  @Input() disabled: boolean = false;

  @Output() filterChange = new EventEmitter<any>();
  @Output() clear = new EventEmitter<void>();

  @ViewChild(MatMenuTrigger) menuTrigger?: MatMenuTrigger;

  get effectivePopoverTitle(): string {
    if (this.popoverTitle) return this.popoverTitle;
    const name = this.labelText ? this.labelText.toLowerCase() : 'status';
    return `Search/Filter by ${name}`;
  }

  get effectiveSearchPlaceholder(): string {
    if (this.searchPlaceholder) return this.searchPlaceholder;
    return `Select ${this.labelText || 'Status'}`;
  }

  selectedValue: any = [];
  tempSelectedValue: any = [];
  searchText: string = '';

  private onChange: (val: any) => void = () => {};
  private onTouched: () => void = () => {};

  constructor(private cdr: ChangeDetectorRef) {}

  writeValue(val: any): void {
    if (this.multiple) {
      this.selectedValue = Array.isArray(val)
        ? [...val]
        : val !== undefined && val !== null && val !== ''
        ? [val]
        : [];
      this.tempSelectedValue = [...this.selectedValue];
    } else {
      this.selectedValue = val !== undefined && val !== null ? val : null;
      this.tempSelectedValue = this.selectedValue;
    }
    this.cdr.markForCheck();
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    this.cdr.markForCheck();
  }

  get isFilterApplied(): boolean {
    if (this.multiple) {
      return Array.isArray(this.selectedValue) && this.selectedValue.length > 0;
    }
    return (
      this.selectedValue !== null &&
      this.selectedValue !== undefined &&
      this.selectedValue !== ''
    );
  }

  get displaySelectedLabel(): string {
    if (!this.isFilterApplied) return '';
    if (this.multiple && Array.isArray(this.selectedValue)) {
      const labels = this.selectedValue.map((val) => {
        const match = this.options.find((opt) => opt.value === val);
        const text = match ? match.label : String(val);
        return text.length > 10 ? text.substring(0, 10) + '...' : text;
      });
      if (labels.length <= 2) {
        return labels.join(', ');
      }
      return `${labels.slice(0, 2).join(', ')} +${labels.length - 2}`;
    }
    const match = this.options.find((opt) => opt.value === this.selectedValue);
    return match ? match.label : String(this.selectedValue);
  }

  get filteredOptions(): StatusFilterOption[] {
    if (!this.searchText.trim()) return this.options;
    const query = this.searchText.toLowerCase().trim();
    return this.options.filter((opt) =>
      opt.label.toLowerCase().includes(query)
    );
  }

  isSelected(val: any): boolean {
    if (this.multiple) {
      return (
        Array.isArray(this.tempSelectedValue) &&
        this.tempSelectedValue.includes(val)
      );
    }
    return this.tempSelectedValue === val;
  }

  get isApplyDisabled(): boolean {
    if (this.multiple) {
      return (
        !Array.isArray(this.tempSelectedValue) ||
        this.tempSelectedValue.length === 0
      );
    }
    return (
      this.tempSelectedValue === null ||
      this.tempSelectedValue === undefined ||
      this.tempSelectedValue === ''
    );
  }

  onMenuOpened(): void {
    this.tempSelectedValue = this.multiple
      ? Array.isArray(this.selectedValue)
        ? [...this.selectedValue]
        : []
      : this.selectedValue;
    this.searchText = '';
    this.cdr.markForCheck();
  }

  onTriggerClick(event?: MouseEvent): void {
    this.tempSelectedValue = this.multiple
      ? Array.isArray(this.selectedValue)
        ? [...this.selectedValue]
        : []
      : this.selectedValue;
    this.searchText = '';
  }

  toggleOption(val: any, event?: Event): void {
    if (event) {
      event.stopPropagation();
    }
    if (this.multiple) {
      if (!Array.isArray(this.tempSelectedValue)) {
        this.tempSelectedValue = [];
      }
      const idx = this.tempSelectedValue.indexOf(val);
      if (idx > -1) {
        this.tempSelectedValue.splice(idx, 1);
      } else {
        this.tempSelectedValue.push(val);
      }
    } else {
      this.tempSelectedValue = val;
    }
    this.cdr.markForCheck();
  }

  applyFilter(event?: Event): void {
    if (event) {
      event.stopPropagation();
    }
    this.selectedValue = this.multiple
      ? Array.isArray(this.tempSelectedValue)
        ? [...this.tempSelectedValue]
        : []
      : this.tempSelectedValue;
    this.onChange(this.selectedValue);
    this.onTouched();
    this.filterChange.emit(this.selectedValue);
    if (this.menuTrigger) {
      this.menuTrigger.closeMenu();
    }
    this.cdr.markForCheck();
  }

  onIconClick(event: MouseEvent): void {
    if (this.isFilterApplied) {
      event.stopPropagation();
      event.preventDefault();
      this.selectedValue = this.multiple ? [] : null;
      this.tempSelectedValue = this.multiple ? [] : null;
      this.onChange(this.selectedValue);
      this.onTouched();
      this.filterChange.emit(this.selectedValue);
      this.clear.emit();
      if (this.menuTrigger) {
        this.menuTrigger.closeMenu();
      }
      this.cdr.markForCheck();
    }
  }
}
